// import cookie from 'js-cookie';
import _ from 'lodash';
import { OutputChannels, OutputChannelsByModelType } from 'src/modules/locale';
import { supportedLocales } from './config';
import { ChannelsFullNames } from './types';
import { useLocale } from './components/Provider/LocaleProvider';
import { LayoutCardsDownloadsData } from 'src/modules/layout/components/block/cardsDownloads/LayoutCardsDownloads';

// For some reason, using LayoutDynamicZoneComponentTypes.CardsDownloadsLayout breaks the unit tests...
// import { LayoutDynamicZoneComponentTypes } from 'src/modules/layout/components/dynamicZone/LayoutDynamicZone';
const LayoutDynamicZoneComponentTypes_CardsDownloadsLayout =
  'ComponentTextImageCardsDownloadsLayout';

// Keep track of the output channel to log it to the console.
// Using this to test issues with output channels, mainly with SSG.
let __logOutputChannel = '';

export const useMatchesChannel = (channel: ChannelsFullNames) => {
  const { outputChannel } = useLocale();

  if (channel) {
    switch (channel) {
      case ChannelsFullNames.Europe:
        if (outputChannel !== OutputChannels.DE) {
          return false;
        }
        break;
      case ChannelsFullNames.Usa:
        if (outputChannel !== OutputChannels.US) {
          return false;
        }
        break;
      case ChannelsFullNames.Australia:
        if (outputChannel !== OutputChannels.AU) {
          return false;
        }
        break;
    }
  }

  return true;
};

export const replaceWalterKnollUSA = (str: string, outputChannel: string) => {
  if (outputChannel === OutputChannels.US) {
    let result = '';
    result = str.replace(
      /(.?)(Walter.Knoll)(.?)/g,
      (_match: string, p1: string, p2: string, p3: string) => {
        // matched [Walter Knoll], return Walter Knoll
        if (p1 === '[' && p3 === ']') return p2;

        // matched "Walter Knoll.", return Walter K. (don't add extra ".")
        if (p3 === '.') return p1 + 'Walter K.';

        // matched "Walter Knoll", return Walter K.
        return p1 + 'Walter K.' + p3;
      }
    );

    result = result.replace(/#WalterKnoll/g, '#WalterK');
    result = result.replace(/#walterknoll/g, '#walterk');

    return result;
  }

  const result = str.replace(/\[Walter Knoll\]/g, 'Walter Knoll');
  return result;
};

/**
 * Get all messages of the specified locale. Defaults to "en".
 *
 * Message key pattern: {module}/{section}/{description}.
 * Eg. header/top/button_search, home/hero/heading
 *
 * @param locale
 */
export function getMessages(locale: string) {
  try {
    return require(`../../../public/locale/${locale}.json`);
  } catch (error) {
    return require(`../../../public/locale/en.json`);
  }
}

export const getLocaleName = (
  item: { name_en: string; name_de: string },
  locale: string = 'en'
): string => item['name_' + locale];

export function getLocalizedObject(
  object: { [s: string]: any },
  locale: string,
  outputChannel: string
) {
  if (outputChannel !== __logOutputChannel) {
    console.log('___ getLocalizedObject() outputChannel:', outputChannel);
    __logOutputChannel = outputChannel;
  }

  const result = {};
  const expression = new RegExp(`(.*)_(${supportedLocales.join('|')})$`);

  if (!object || _.isNumber(object)) {
    return object;
  }
  Object.keys(object).forEach((key) => {
    const matches = key.match(expression);

    if (matches) {
      const [, keyPlain, keyLocale] = matches;

      if (keyLocale === locale) {
        if (_.isArray(object[key])) {
          result[keyPlain] = getLocalizedObjects(object[key], locale, outputChannel);
        } else if (_.isObject(object[key])) {
          result[keyPlain] = getLocalizedObject(object[key], locale, outputChannel);
        } else if (_.isString(object[key]) && !object[key].includes('http')) {
          result[keyPlain] = replaceWalterKnollUSA(object[key], outputChannel);
        }

        //Replace S3 URLs with CDN URLs
        else if (
          _.isString(object[key]) &&
          object[key].includes('s3.eu-central-1.amazonaws.com') &&
          process.env.NEXT_PUBLIC_CDN_URL
        ) {
          result[keyPlain] =
            process.env.NEXT_PUBLIC_CDN_URL +
            _.last(object[key].split('s3.eu-central-1.amazonaws.com'));
        } else {
          result[keyPlain] = object[key];
        }
      }
    } else {
      if (_.isArray(object[key])) {
        result[key] = getLocalizedObjects(object[key], locale, outputChannel);
      } else if (_.isFunction(object[key])) {
        result[key] = object[key];
      } else if (_.isObject(object[key])) {
        result[key] = getLocalizedObject(object[key], locale, outputChannel);
      } else if (_.isString(object[key]) && !object[key].includes('http')) {
        result[key] = replaceWalterKnollUSA(object[key], outputChannel);
      }
      //Replace S3 URLs with CDN URLs
      else if (
        _.isString(object[key]) &&
        object[key].includes('s3.eu-central-1.amazonaws.com') &&
        process.env.NEXT_PUBLIC_CDN_URL
      ) {
        result[key] =
          process.env.NEXT_PUBLIC_CDN_URL +
          _.last(object[key].split('s3.eu-central-1.amazonaws.com'));
      }
      //There is an outstanding bug with product subcategories in the PLP, which get converted from String to array
      // else if (_.isString(object[key])) {
      //   return object;
      // }
      else {
        result[key] = object[key];
      }
    }
  });

  return result;
}

export function getLocalizedObjects(
  objects: { [s: string]: any }[],
  locale: string,
  outputChannel: string
) {
  return objects ? objects.map((object) => getLocalizedObject(object, locale, outputChannel)) : [];
}

/**
 * Get the `model` that matches the current outputChannel
 */
export function getModelByOutputChannel<T extends { channel?: 'usa' | 'australia' | 'europe' }>(
  model: T[] | T,
  outputChannel: 'de_DE' | 'en_US' | 'en_AU'
): T | undefined {
  const modelArray = _.isArray(model) ? model : [model];

  const modelByChannel = _.find(modelArray, (singleModel) => {
    const channel = singleModel?.channel;
    if (!channel) return false;

    return OutputChannelsByModelType[channel] === outputChannel;
  });

  return modelByChannel;
}

export type Channel = 'usa' | 'australia' | 'europe' | 'europe_australia';
type Model = {
  channel?: Channel;
  // NOTE: dynamic componets channel field need aliases for the GraphQL query
  // to parse correctly. Look at dynamicZone/queries.ts
  text_image_card_channel?: Channel;
  text_image_card_download_channel?: Channel;
  media_video_channel?: Channel;
  media_side_by_side_images_channel?: Channel;
  text_and_image_channel?: Channel;
  hero_channel?: Channel;
  full_width_hero_channel?: Channel;
  multi_image_layout_channel?: Channel;
  text_quote_channel?: Channel;
  text_block_channel?: Channel;
  media_image_channel?: Channel;
  media_gallery_channel?: Channel;
  cta_button_channel?: Channel;
  product_in_context_channel?: Channel;
};

const filterDownloadCardsByOutputChannel = (
  _model: Model,
  outputChannel: 'de_DE' | 'en_US' | 'en_AU'
) => {
  const model = (_model as unknown) as LayoutCardsDownloadsData;

  const hasDownloadCards =
    model.__typename === LayoutDynamicZoneComponentTypes_CardsDownloadsLayout &&
    !_.isEmpty(model.cards);

  if (hasDownloadCards) {
    const filteredCards = filterModelsByOutputChannel(model.cards, outputChannel);
    model.cards = filteredCards;
  }
};

/**
 * Get all the `models` that matches the current outputChannel
 */
export function filterModelsByOutputChannel<T extends Model>(
  models: T[],
  outputChannel: 'de_DE' | 'en_US' | 'en_AU'
): T[] {
  const filteredModels = _.filter(models, (m) => {
    const channel =
      m?.text_image_card_channel ||
      m?.media_video_channel ||
      m?.media_side_by_side_images_channel ||
      m?.text_and_image_channel ||
      m?.hero_channel ||
      m?.full_width_hero_channel ||
      m?.multi_image_layout_channel ||
      m?.text_quote_channel ||
      m?.text_block_channel ||
      m?.media_image_channel ||
      m?.media_gallery_channel ||
      m?.cta_button_channel ||
      m?.product_in_context_channel ||
      m?.text_image_card_download_channel ||
      m?.channel;

    filterDownloadCardsByOutputChannel(m, outputChannel);

    if (!channel) return true;

    if (channel === 'europe_australia' && _.includes(['de_DE', 'en_AU'], outputChannel))
      return true;

    return OutputChannelsByModelType[channel] === outputChannel;
  });

  return filteredModels;
}
